var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import './vitesse-icon';
import './vitesse-logo';
import { twoPaneSelectStyles } from './vitesse-model-select';
export const PLATFORM_NAME = {
    ai: 'AI',
    app: 'App',
    fleet: 'Fleet',
};
const ORDER = ['ai', 'app', 'fleet'];
let VitesseToolSelect = class VitesseToolSelect extends LitElement {
    constructor() {
        super(...arguments);
        this.tools = [];
        this.value = '';
        this.placeholder = 'Pick a tool';
        this.disabled = false;
        /** Command-palette mode: picking emits `change` but the trigger stays on the placeholder. */
        this.stateless = false;
        this.open = false;
        this.active = null;
        this.positioned = false;
        this.onDocClick = (e) => {
            if (this.open && !e.composedPath().includes(this))
                this.open = false;
        };
        this.onKey = (e) => {
            if (e.key === 'Escape')
                this.open = false;
        };
        this.reposition = () => {
            if (this.open)
                this.position();
        };
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        this.removeReposition();
    }
    platforms() {
        const present = new Set(this.tools.map((t) => t.platform));
        return ORDER.filter((p) => present.has(p));
    }
    toolsFor(p) {
        return p ? this.tools.filter((t) => t.platform === p) : [];
    }
    selected() {
        return this.tools.find((t) => t.id === this.value);
    }
    toggleOpen() {
        this.open = !this.open;
        if (this.open) {
            const sel = this.selected();
            const plats = this.platforms();
            this.active = sel?.platform ?? plats[0] ?? null;
        }
    }
    pick(id) {
        this.open = false;
        if (!this.stateless)
            this.value = id;
        this.dispatchEvent(new CustomEvent('change', { detail: { value: id }, bubbles: true, composed: true }));
    }
    // Identical popover mechanics to <vitesse-model-select>: position ONCE per open; the
    // panel height is dictated by the RAIL (platform count, min 4 / max 8 rows).
    updated() {
        const p = this.panelEl;
        if (!p)
            return;
        if (this.open) {
            if (!this.positioned) {
                try {
                    p.showPopover();
                }
                catch {
                    /* already shown */
                }
                this.position();
                this.positioned = true;
                window.addEventListener('scroll', this.reposition, true);
                window.addEventListener('resize', this.reposition);
            }
        }
        else if (this.positioned) {
            try {
                p.hidePopover();
            }
            catch {
                /* already hidden */
            }
            this.removeReposition();
            this.positioned = false;
        }
    }
    removeReposition() {
        window.removeEventListener('scroll', this.reposition, true);
        window.removeEventListener('resize', this.reposition);
    }
    position() {
        const t = this.triggerEl;
        const p = this.panelEl;
        if (!t || !p)
            return;
        const ROW = 32;
        const PAD = 10;
        const railH = this.platforms().length * ROW + PAD;
        p.style.height = `${Math.max(4 * ROW + PAD, Math.min(8 * ROW + PAD, railH))}px`;
        const r = t.getBoundingClientRect();
        p.style.position = 'fixed';
        p.style.margin = '0';
        p.style.inset = 'auto';
        const w = p.offsetWidth || 340;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
        p.style.left = `${left}px`;
        const below = window.innerHeight - r.bottom;
        const need = p.offsetHeight + 12;
        if (below < need && r.top > below) {
            p.style.top = 'auto';
            p.style.bottom = `${window.innerHeight - r.top + 6}px`;
        }
        else {
            p.style.bottom = 'auto';
            p.style.top = `${r.bottom + 6}px`;
        }
    }
    static { this.styles = [
        twoPaneSelectStyles,
        css `
      /* Tool descriptions may run long — let them wrap is wrong (row height must stay fixed);
         ellipsize instead, the full text is the row's title attr. */
      .mlbl .caps {
        max-width: 236px;
      }
    `,
    ]; }
    render() {
        const sel = this.selected();
        const plats = this.platforms();
        const activeTools = this.toolsFor(this.active);
        return html `
      <div
        class="trigger ${this.open ? 'open' : ''}"
        role="button"
        tabindex=${this.disabled ? -1 : 0}
        @click=${this.toggleOpen}
        @keydown=${(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggleOpen();
            }
        }}
      >
        ${sel
            ? html `<vitesse-logo compact .size=${26}></vitesse-logo><span class="val">${sel.label}</span>`
            : html `<span class="val ph">${this.placeholder}</span>`}
        <vitesse-icon class="chev ${this.open ? 'open' : ''}" name="chevron-down" size="16"></vitesse-icon>
      </div>
      <div class="panel" popover="manual">
        ${plats.length === 0
            ? html `<div class="empty">${this.placeholder}</div>`
            : html `
              <div class="rail">
                ${plats.map((p) => html `<button
                    type="button"
                    class="prov ${p === this.active ? 'active' : ''}"
                    @mouseenter=${() => (this.active = p)}
                    @click=${() => (this.active = p)}
                  >
                    <vitesse-logo compact .size=${26}></vitesse-logo>
                    <span>${PLATFORM_NAME[p]}</span>
                  </button>`)}
              </div>
              <div class="models">
                ${activeTools.length === 0
                ? html `<div class="empty">—</div>`
                : activeTools.map((t) => html `<button
                        type="button"
                        class="mrow ${t.id === this.value ? 'sel' : ''}"
                        title=${t.desc ?? ''}
                        @click=${() => this.pick(t.id)}
                      >
                        <span class="mlbl"><span class="name">${t.label}</span>${t.desc ? html `<span class="caps">${t.desc}</span>` : nothing}</span>
                      </button>`)}
              </div>
            `}
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseToolSelect.prototype, "tools", void 0);
__decorate([
    property()
], VitesseToolSelect.prototype, "value", void 0);
__decorate([
    property()
], VitesseToolSelect.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseToolSelect.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], VitesseToolSelect.prototype, "stateless", void 0);
__decorate([
    state()
], VitesseToolSelect.prototype, "open", void 0);
__decorate([
    state()
], VitesseToolSelect.prototype, "active", void 0);
__decorate([
    query('.trigger')
], VitesseToolSelect.prototype, "triggerEl", void 0);
__decorate([
    query('.panel')
], VitesseToolSelect.prototype, "panelEl", void 0);
__decorate([
    query('.rail')
], VitesseToolSelect.prototype, "railEl", void 0);
VitesseToolSelect = __decorate([
    customElement('vitesse-tool-select')
], VitesseToolSelect);
export { VitesseToolSelect };
//# sourceMappingURL=vitesse-tool-select.js.map